import React from 'react';
import {
  Switch,
  FormControlLabel,
  FormHelperText,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Stack,
} from '@mui/material';

/**
 * Design System Color Constants
 */
const COLORS = {
  ACCENT: '#005e5d',
  ACCENT_DARK: '#004d4c',
  ACCENT_LIGHT: '#005e5d14',
  SURFACE_SECONDARY: '#F2F3F5',
  BORDER: '#E6E7EB',
  TEXT_PRIMARY: '#333333',
  TEXT_SECONDARY: '#666666',
  TEXT_TERTIARY: '#999999',
  DESTRUCTIVE: '#D32F2F',
  SUCCESS: '#49CC90',
  DISABLED: '#D0D2D7',
};

export interface SwitchOption {
  label: string;
  value: string | number;
  description?: string;
  disabled?: boolean;
}

export interface BaseSwitchProps {
  /**
   * Label for the switch
   */
  label?: string;
  /**
   * Is switch enabled
   */
  checked?: boolean;
  /**
   * Change handler
   */
  onChange?: (checked: boolean) => void;
  /**
   * Helper text
   */
  helperText?: string;
  /**
   * Description text
   */
  description?: string;
  /**
   * Is field required
   */
  required?: boolean;
  /**
   * Show error state
   */
  error?: boolean;
  /**
   * Disable switch
   */
  disabled?: boolean;
  /**
   * Size of switch
   */
  size?: 'small' | 'medium';
  /**
   * Color scheme
   */
  colorVariant?: 'primary' | 'success' | 'warning' | 'error';
}

export interface BaseSwitchGroupProps {
  /**
   * Array of switch options for group
   */
  options: SwitchOption[];
  /**
   * Selected values
   */
  values: (string | number)[];
  /**
   * Change handler
   */
  onChange?: (values: (string | number)[]) => void;
  /**
   * Label for the group
   */
  label?: string;
  /**
   * Helper text
   */
  helperText?: string;
  /**
   * Layout direction
   */
  direction?: 'row' | 'column';
  /**
   * Is field required
   */
  required?: boolean;
  /**
   * Show error state
   */
  error?: boolean;
  /**
   * Disable all switches
   */
  disabled?: boolean;
  /**
   * Show descriptions
   */
  showDescriptions?: boolean;
  /**
   * Spacing between items
   */
  spacing?: number;
}

/**
 * Get switch color based on variant
 */
const getSwitchColor = (
  colorVariant: 'primary' | 'success' | 'warning' | 'error' = 'primary'
): string => {
  const colorMap = {
    primary: COLORS.ACCENT,
    success: COLORS.SUCCESS,
    warning: '#FCA130',
    error: COLORS.DESTRUCTIVE,
  };
  return colorMap[colorVariant];
};

/**
 * BaseSwitch Component
 *
 * Premium toggle switch component following the Pulse API design system.
 * Supports both single switch and switch groups.
 *
 * @example
 * ```tsx
 * // Single switch
 * <BaseSwitch
 *   label="Enable feature"
 *   checked={enabled}
 *   onChange={setEnabled}
 * />
 *
 * // Switch with description
 * <BaseSwitch
 *   label="Dark mode"
 *   description="Use dark theme for the application"
 *   checked={darkMode}
 *   onChange={setDarkMode}
 * />
 *
 * // Switch group
 * <BaseSwitchGroup
 *   label="Permissions"
 *   options={[
 *     { label: 'Read', value: 'read' },
 *     { label: 'Write', value: 'write' },
 *   ]}
 *   values={selected}
 *   onChange={setSelected}
 * />
 * ```
 */
export const BaseSwitch = React.forwardRef<HTMLDivElement, BaseSwitchProps>(
  (
    {
      label,
      checked = false,
      onChange,
      helperText,
      description,
      required = false,
      error = false,
      disabled = false,
      size = 'medium',
      colorVariant = 'primary',
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event.target.checked);
    };

    const switchColor = getSwitchColor(colorVariant);

    return (
      <Box ref={ref} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={handleChange}
              disabled={disabled}
              required={required}
              size={size}
              sx={{
                '& .MuiSwitch-switchBase': {
                  color: COLORS.DISABLED,
                  '&.Mui-checked': {
                    color: switchColor,
                    '& + .MuiSwitch-track': {
                      backgroundColor: switchColor,
                      opacity: 1,
                    },
                  },
                  '&.Mui-disabled': {
                    color: COLORS.SURFACE_SECONDARY,
                    '& + .MuiSwitch-track': {
                      backgroundColor: COLORS.BORDER,
                    },
                  },
                },
                '& .MuiSwitch-track': {
                  backgroundColor: COLORS.BORDER,
                  opacity: 1,
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
          }
          label={
            label && (
              <Box sx={{ ml: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    color: disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
                    fontFamily: '"Manrope", sans-serif',
                  }}
                >
                  {label}
                  {required && <span style={{ color: COLORS.DESTRUCTIVE }}>*</span>}
                </Typography>
                {description && (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: COLORS.TEXT_TERTIARY,
                      fontWeight: 400,
                      mt: 0.25,
                      fontFamily: '"Manrope", sans-serif',
                    }}
                  >
                    {description}
                  </Typography>
                )}
              </Box>
            )
          }
          sx={{
            mb: 0,
            width: '100%',
          }}
        />

        {helperText && (
          <Typography
            sx={{
              fontSize: '11px',
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_TERTIARY,
              fontWeight: 500,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {helperText}
          </Typography>
        )}
      </Box>
    );
  }
);

BaseSwitch.displayName = 'BaseSwitch';

/**
 * BaseSwitchGroup Component
 *
 * Multiple switches grouped together with consistent styling.
 */
export const BaseSwitchGroup: React.FC<BaseSwitchGroupProps> = ({
  options,
  values,
  onChange,
  label,
  helperText,
  direction = 'column',
  required = false,
  error = false,
  disabled = false,
  showDescriptions = false,
  spacing = 1.5,
}) => {
  const handleChange = (optionValue: string | number) => {
    const newValues = values.includes(optionValue)
      ? values.filter((v) => v !== optionValue)
      : [...values, optionValue];
    onChange?.(newValues);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <FormControl
        disabled={disabled}
        error={error}
        component="fieldset"
        required={required}
      >
        {label && (
          <FormLabel
            sx={{
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_PRIMARY,
              fontWeight: 600,
              fontSize: '13px',
              mb: 1,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {label}
            {required && <span style={{ color: COLORS.DESTRUCTIVE }}>*</span>}
          </FormLabel>
        )}

        <Stack
          direction={direction}
          spacing={spacing}
          sx={{
            display: 'flex',
            flexDirection: direction,
            gap: spacing,
          }}
        >
          {options.map((option, index) => (
            <FormControlLabel
              key={index}
              control={
                <Switch
                  checked={values.includes(option.value)}
                  onChange={() => handleChange(option.value)}
                  disabled={option.disabled || disabled}
                  sx={{
                    '& .MuiSwitch-switchBase': {
                      color: COLORS.DISABLED,
                      '&.Mui-checked': {
                        color: COLORS.ACCENT,
                        '& + .MuiSwitch-track': {
                          backgroundColor: COLORS.ACCENT,
                        },
                      },
                      '&.Mui-disabled': {
                        color: COLORS.SURFACE_SECONDARY,
                        '& + .MuiSwitch-track': {
                          backgroundColor: COLORS.BORDER,
                        },
                      },
                    },
                    '& .MuiSwitch-track': {
                      backgroundColor: COLORS.BORDER,
                    },
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                />
              }
              label={
                <Box sx={{ ml: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: option.disabled ? 400 : 500,
                      color: option.disabled ? COLORS.TEXT_TERTIARY : COLORS.TEXT_PRIMARY,
                      fontFamily: '"Manrope", sans-serif',
                    }}
                  >
                    {option.label}
                  </Typography>
                  {showDescriptions && option.description && (
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: COLORS.TEXT_TERTIARY,
                        fontWeight: 400,
                        mt: 0.25,
                        fontFamily: '"Manrope", sans-serif',
                      }}
                    >
                      {option.description}
                    </Typography>
                  )}
                </Box>
              }
              sx={{
                mb: 0,
                width: '100%',
              }}
            />
          ))}
        </Stack>

        {helperText && (
          <FormHelperText
            sx={{
              color: error ? COLORS.DESTRUCTIVE : COLORS.TEXT_TERTIARY,
              fontSize: '11px',
              fontWeight: 500,
              mt: 1,
              fontFamily: '"Manrope", sans-serif',
            }}
          >
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

BaseSwitchGroup.displayName = 'BaseSwitchGroup';

export default BaseSwitch;
